import { isString as lodashIsString, isFunction as lodashIsFunction, merge as lodashMerge, cloneDeep } from 'lodash-es'
import { UpperLockStatus } from '@/constant/data'
import { nanoid } from 'nanoid'
import { currencyHandle, formatDate, formatSecondStr, toRate, toWanHandle, formatVt } from '@Utils/business'
import { ACCOUNT_STATUS_ENUM } from '@Constant/authStatus'

/**
 * @description 检测类型
 * @param obj
 * @returns {*}
 */
export function typeOf(obj) {
  const toString = Object.prototype.toString
  const map = {
    '[object Boolean]': 'boolean',
    '[object Number]': 'number',
    '[object String]': 'string',
    '[object Function]': 'function',
    '[object Array]': 'array',
    '[object Date]': 'date',
    '[object RegExp]': 'regExp',
    '[object Undefined]': 'undefined',
    '[object Null]': 'null',
    '[object Object]': 'object'
  }
  return map[toString.call(obj)]
}

/**
 * @description
 * @param obj
 */
export function isString(obj) {
  return lodashIsString(obj)
}

/**
 * @description
 * @param object
 * @returns {*}
 */
export function isNumber(object) {
  return typeOf(object) === 'number'
}

/**
 * @description
 * @param object
 * @returns {*}
 */
export function isFunction(object) {
  return lodashIsFunction(object)
}

/**
 * @description
 * @param object
 * @returns {*}
 */
export function isArray(object) {
  return typeOf(object) === 'array'
}

/**
 * @description
 * @param object
 * @returns {*}
 */
export function isObject(object) {
  return typeOf(object) === 'object'
}

/**
 * @description
 * @param object
 * @returns {*}
 */
export function isDate(object) {
  return typeOf(object) === 'date'
}

/**
 * @description
 * @param value
 * @returns {*}
 */
export function isUndefined(value) {
  return typeOf(value) === 'undefined'
}

/**
 * @description
 * @param value
 * @returns {*}
 */
export function isNull(value) {
  return typeOf(value) === 'null'
}

/**
 *
 * @param target 目标对象
 * @param source 源对象
 */
export function merge(target, source) {
  return lodashMerge(target, source)
}

export function deepCopy(value) {
  return cloneDeep(value)
}

// 判断参数是否是其中之一
export function oneOf(value, validList) {
  for (let i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true
    }
  }
  return false
}

// 是否是纪念账号
export function isAnniversaryAccount(upperBaseStatus) {
  return [UpperLockStatus.Anniversary, UpperLockStatus.ShortTermDisableAndMemo, UpperLockStatus.PermanentDisableAndMemo].includes(upperBaseStatus)
}

// 是否是永久封禁+纪念
export function isAnniversaryPermanentDisable(upperBaseStatus) {
  return isAnniversaryAccount(upperBaseStatus) || upperBaseStatus === UpperLockStatus.PermanentDisable
}

// 是否是短期封禁
export function isShortTermDisable(upperBaseStatus) {
  return upperBaseStatus === UpperLockStatus.ShortTermDisable
}

export function getDateFromString(str) {
  const formatStr = str.replace(/-/g, '/')

  return new Date(formatStr)
}

// eslint-disable-next-line
const SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g
const MOZ_HACK_REGEXP = /^moz([A-Z])/

function camelCase(name) {
  return name
    .replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
      return offset ? letter.toUpperCase() : letter
    })
    .replace(MOZ_HACK_REGEXP, 'Moz$1')
}

export function getStyle(element, styleName) {
  if (!element || !styleName) return null
  styleName = camelCase(styleName)
  if (styleName === 'float') {
    styleName = 'cssFloat'
  }
  try {
    const computed = document.defaultView.getComputedStyle(element, '')
    return element.style[styleName] || computed ? computed[styleName] : null
  } catch (e) {
    return element.style[styleName]
  }
}

export function isDef(val) {
  return val !== undefined && val !== null
}

export function addUnit(value) {
  if (!isDef(value)) {
    return undefined
  }

  return isNumber(value) ? `${value}px` : String(value)
}

// 仅针对邀约数据权限判断（临时）
export function isAccessibleForReport(accountId) {
  const testIds = []

  const realIds = [
    503408, 503407, 503310, 503309, 679575, 689092, 601570, 428052, 165387, 342596, 613901, 489585, 493283, 364648, 164990, 431241, 674944, 706054, 165341, 165387, 601570, 428052, 337387, 1013062,
    1013064, 1013047, 1014436, 395890
  ]

  return [...realIds, ...testIds].includes(accountId)
}

export function deepEqual(val1, val2) {
  if (val1 === val2) return true
  if (typeof val1 === 'object') {
    for (const key in val1) {
      if (!deepEqual(val1[key], val2[key])) {
        return false
      }
    }
    return true
  }
  return false
}

export function pick(obj, keys) {
  return keys.map(k => (k in obj ? { [k]: obj[k] } : {})).reduce((res, o) => Object.assign(res, o), {})
}

export function reject(obj, keys) {
  const vkeys = Object.keys(obj).filter(k => !keys.includes(k))
  return pick(obj, vkeys)
}

export function formatSecond(second, option) {
  let d = 0
  let h = 0
  let m = 0
  let result = ''

  const options = { year: true, day: true, hour: true, minute: true, second: true }
  const padLeft = function (n = '') {
    return n > 9 ? n : '0' + n
  }
  //
  for (const prop in option) {
    options[prop] = option[prop]
  }

  if (second > 86400) {
    d = Math.floor(second / 86400)
    second = second % 86400
  }
  if (second > 3600) {
    h = Math.floor(second / 3600)
    second = second % 3600
  }
  if (second > 60) {
    m = Math.floor(second / 60)
    second = second % 60
  }
  if (options.second) {
    result = padLeft(second)
  }
  if (options.minute) {
    if (m >= 0 || h >= 0 || d >= 0) {
      result = padLeft(m) + ':' + result
    }
  }
  if (options.hour) {
    if (h > 0 || d > 0) {
      result = padLeft(h) + ':' + result
    }
  }
  if (options.day && d > 0) {
    result = padLeft(d) + ' ' + result
  }
  return result
}

export const trimProtocol = url => {
  return url.replace(/^https?:/g, '')
}

export const loadScript = (src, scriptId) => {
  const box = document

  return new Promise(resolve => {
    let scriptEl = document.getElementById(scriptId)
    if (scriptEl && scriptEl.parent) {
      resolve('')
    }
    scriptEl = box.createElement('script')
    scriptEl.id = scriptId
    scriptEl.type = 'text/JavaScript'
    scriptEl.src = src
    scriptEl.onload = () => resolve()
    scriptEl.onreadystatechange = function () {
      if (!this.readyState || this.readyState === 'loaded' || this.readyState === 'complete') {
        resolve()
      }
    }
    box.querySelector('head').appendChild(scriptEl)
  })
}

/**
 * @description 获取叶子节点
 * @param array
 * @param childrenName
 * @returns array
 */
export function getChildrenNode(array, childrenName = 'children') {
  if (!array || !array.length) {
    return []
  }
  let childrenArray = []
  array.forEach(obj => {
    if (obj[childrenName] && obj[childrenName].length) {
      childrenArray = [...childrenArray, ...getChildrenNode(obj[childrenName])]
    } else {
      childrenArray.push(obj)
    }
  })
  return childrenArray
}

export function numAdd(arg1, arg2) {
  let r1, r2, m
  try {
    r1 = arg1.toString().split('.')[1].length
  } catch (e) {
    r1 = 0
  }
  try {
    r2 = arg2.toString().split('.')[1].length
  } catch (e) {
    r2 = 0
  }
  m = Math.pow(10, Math.max(r1, r2))
  return (arg1 * m + arg2 * m) / m
}

export function createUuid(size = 8) {
  return nanoid(size)
}
export function replaceUrl(url = '') {
  return url.replace(/^https?:/g, 'https:')
}

// filters
export function currencyHandleStr(value, currency = '', decimals = 0, unit = '万') {
  return currencyHandle(value, currency || '', decimals || 0)
}
export function toWanHandleStr(value, currency = '', decimals = 1, unit = '万') {
  return toWanHandle(value, currency, decimals, unit)
}
export function formatDateHandle(value, formator, sign) {
  return formatDate(value, formator, sign)
}
export function toRateHandle(value, base, decimals) {
  return toRate(value, base, decimals)
}
export function formatSecondHandle(value, option) {
  return formatSecondStr(value, option)
}
export function authWanHandle(value, accountAuditStatus, currency = '', decimals = 1, unit = '万') {
  if ([ACCOUNT_STATUS_ENUM.CERTIFIED, ACCOUNT_STATUS_ENUM.WILL_EXPIRE, ACCOUNT_STATUS_ENUM.EXPIRED].includes(accountAuditStatus)) {
    return toWanHandle(value, currency, decimals, unit)
  }
  return '**'
}
export function toVtHandle(value, unit = '', placeholder = '--') {
  return formatVt(value, { placeholder, unit })
}

export function highlightText(text, keyword, styleAttribute) {
  // 转义正则表达式中的特殊字符
  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // 创建匹配正则表达式，使用i标志实现不区分大小写
  const regex = new RegExp(`(${escapedKeyword})`, 'gi')

  // 使用replace将匹配到的关键词用高亮标签包裹
  return text.replace(regex, `<span ${styleAttribute}>$1</span>`)
}
